import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Typewriter from 'typewriter-effect';
import {
  ArrowRight, ChevronDown, Brain, Globe, ShoppingCart,
  Wrench, Smartphone, Layers, Zap, ExternalLink, X,
} from 'lucide-react';
import ContactForm from '../components/ContactForm';
import { useWindowWidth } from '../hooks/useWindowWidth';


const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (d: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut', delay: d },
  }),
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const services = [
  { icon: <Brain size={22} />, title: 'Custom AI', desc: 'Chatbots, content generators, and AI-powered tools tailored to your business workflow.', color: '#7c3aed' },
  { icon: <Globe size={22} />, title: 'Web Development', desc: 'Fast, SEO-optimized websites and web applications built with modern frameworks.', color: '#2563eb' },
  { icon: <Wrench size={22} />, title: 'Custom Software', desc: 'Bespoke internal tools, portals, and platforms built to solve real operational problems.', color: '#0d9488' },
  { icon: <ShoppingCart size={22} />, title: 'E-Commerce', desc: 'Stores that convert — with custom product flows, integrations, and secure payments.', color: '#059669' },
  { icon: <Smartphone size={22} />, title: 'App Creation', desc: 'iOS, Android, and cross-platform apps built for speed and usability.', color: '#0891b2' },
  { icon: <Layers size={22} />, title: 'Web3 & Blockchain', desc: 'DApps, smart contracts, and decentralized infrastructure for the next web.', color: '#6d4aff' },
];

const stats = [
  { value: '166+', label: 'Custom GPTs Built' },
  { value: '25+', label: 'Client Projects' },
  { value: '2×', label: 'Press Features' },
  { value: '2018', label: 'Founded' },
];

interface FeaturedProject {
  title: string;
  subtitle: string;
  description: string;
  gradientBg: string;
  link: string;
  isInternal?: boolean;
  isPrivate?: boolean;
  featured: boolean;
}

const featuredProjects: FeaturedProject[] = [
  {
    title: 'Custom AI Demo',
    subtitle: 'AI Chatbot',
    description: 'Interactive AI assistant showcasing real-world applications of large language models. Ask it anything about Elola Technologies, our services, or digital solutions in general.',
    gradientBg: 'linear-gradient(135deg, #1a0a3d 0%, #4b2aad 50%, #0d0628 100%)',
    link: '/chat',
    isInternal: true,
    featured: true,
  },
  {
    title: 'AI GPT Creations',
    subtitle: '166+ GPTs',
    description: "Marcus S. Elola's growing library of 166+ custom GPTs with tens of thousands of combined uses — from Numerology to Rhyme Master, Breakup Guide to Suicide Support. Featured in GQ and the San Francisco Standard.",
    gradientBg: 'linear-gradient(135deg, #0d1a3d 0%, #1e3a8a 50%, #091326 100%)',
    link: '/GPTs',
    isInternal: true,
    featured: true,
  },
  {
    title: 'X-Ray Contractor',
    subtitle: 'Contractor SaaS · AI',
    description: 'Full-stack SaaS platform for construction contractors — bid management, estimates, invoices, CRM, timekeeping, payroll, job site tracking, and a god mode admin portal for multi-tenant oversight. Built on Next.js 16, Supabase, Stripe, and Vercel.',
    gradientBg: 'linear-gradient(135deg, #0a1a2e 0%, #1a4a7a 50%, #061020 100%)',
    link: 'https://vbx-bid-automation.vercel.app/',
    featured: true,
  },
  {
    title: 'Express Tracker',
    subtitle: 'Auto Dealer Contract Platform',
    description: 'Web platform for auto dealerships to submit and track vehicle service contracts. Dealers submit contracts through a secure portal; admins monitor submissions across the full dealer network with PDF and Excel export. Built with React 19, Firebase, and Tailwind.',
    gradientBg: 'linear-gradient(135deg, #0f1a0a 0%, #2a5c1a 50%, #081009 100%)',
    link: '',
    isPrivate: true,
    featured: true,
  },
  {
    title: 'Perfect Reflections',
    subtitle: 'Exotic Auto Body',
    description: 'Premium website for a Bay Area exotic collision repair specialist serving Ferrari, Lamborghini, McLaren, Porsche, and Aston Martin owners since 1986. Clean, high-end design built to match the caliber of the work.',
    gradientBg: 'linear-gradient(135deg, #1e3a5f 0%, #2d6a9f 50%, #1a2e4a 100%)',
    link: 'https://www.perfectreflections.us/',
    featured: true,
  },
  {
    title: 'CleanView USA',
    subtitle: 'Cleaning Services',
    description: 'Professional website for a cleaning services company — featuring service listings, instant quote requests, and a clean modern design built for local lead generation.',
    gradientBg: 'linear-gradient(135deg, #0a2a1a 0%, #1a6b3d 50%, #061a10 100%)',
    link: 'https://www.cleanviewusa.com/',
    featured: true,
  },
];

const Home: React.FC = () => {
  const isMobile = useWindowWidth() < 768;
  const [activeProject, setActiveProject] = useState<FeaturedProject | null>(null);

  useEffect(() => {
    if (activeProject) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [activeProject]);

  return (
    <>
      <Helmet>
        <title>Elola Technologies | Marcus S. Elola – Web, AI, and Blockchain Solutions</title>
        <meta name="description" content="Elola Technologies provides expert website development, Web3 integration, NFT curation, custom AI solutions, e-commerce, blockchain, and app development." />
        <link rel="canonical" href="https://elola.tech" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Elola Technologies" />
        <meta property="og:title" content="Elola Technologies | Marcus S. Elola – Web, AI, and Blockchain Solutions" />
        <meta property="og:description" content="Digital solutions by Marcus S. Elola: custom AI, Web3, blockchain, app development, and e-commerce." />
        <meta property="og:image" content="https://elola.tech/preview-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content="https://elola.tech" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@elolatech" />
        <meta name="twitter:title" content="Elola Technologies | Web, AI, and Blockchain Solutions" />
        <meta name="twitter:description" content="Digital solutions by Marcus S. Elola: custom AI, Web3, blockchain, app development, and e-commerce." />
        <meta name="twitter:image" content="https://elola.tech/preview-image.png" />
      </Helmet>

      <div style={{ background: 'var(--bg-base)', overflowX: 'hidden' }}>

        {/* ====================== HERO ====================== */}
        <section style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: `calc(var(--navbar-height) + 40px) var(--page-padding-x) 80px`,
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Background orbs */}
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.25, 0.45, 0.25] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              position: 'absolute', top: '-10%', left: '-15%',
              width: isMobile ? '400px' : '700px', height: isMobile ? '400px' : '700px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(109,74,255,0.35) 0%, transparent 70%)',
              filter: 'blur(80px)',
              pointerEvents: 'none',
            }}
          />
          <motion.div
            animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.3, 0.15] }}
            transition={{ duration: 12, delay: 2, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              position: 'absolute', bottom: '-5%', right: '-10%',
              width: isMobile ? '350px' : '600px', height: isMobile ? '350px' : '600px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(245,158,11,0.2) 0%, transparent 70%)',
              filter: 'blur(80px)',
              pointerEvents: 'none',
            }}
          />

          <div style={{ position: 'relative', zIndex: 1, maxWidth: '800px' }}>
            <motion.div custom={0} variants={fadeUp} initial="hidden" animate="visible">
              <div className="section-tag" style={{ justifyContent: 'center', marginBottom: '24px' }}>
                <Zap size={13} />
                AI · Web · Software · E-Commerce
              </div>
            </motion.div>

            <motion.h1
              custom={0.15}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              style={{
                fontSize: 'var(--text-hero)',
                fontWeight: 900,
                lineHeight: 1.1,
                letterSpacing: '-0.03em',
                marginBottom: '12px',
              }}
            >
              Turning Ideas Into
            </motion.h1>

            <motion.h1
              custom={0.25}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              style={{
                fontSize: 'var(--text-hero)',
                fontWeight: 900,
                lineHeight: 1.1,
                letterSpacing: '-0.03em',
                marginBottom: '24px',
              }}
            >
              <span className="gradient-text-gold">Digital Gold.</span>
            </motion.h1>

            <motion.div
              custom={0.35}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              style={{
                fontSize: 'clamp(16px, 2vw, 20px)',
                color: 'var(--accent-purple-light)',
                fontWeight: 500,
                marginBottom: '16px',
                minHeight: '28px',
              }}
            >
              <Typewriter
                options={{
                  strings: [
                    'Custom AI',
                    'Agentic Workflows',
                    'Website Development',
                    'Custom Software Tools',
                    'E-Commerce Integration',
                    'App Creation',
                    'AI Chatbots',
                    'Web3 & Blockchain',
                  ],
                  autoStart: true,
                  loop: true,
                  deleteSpeed: 50,
                  delay: 60,
                }}
              />
            </motion.div>

            <motion.p
              custom={0.45}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              style={{
                fontSize: 'clamp(15px, 1.5vw, 17px)',
                color: 'var(--text-secondary)',
                maxWidth: '560px',
                margin: '0 auto 36px',
                lineHeight: '1.75',
              }}
            >
              We transform ideas into high-impact digital solutions that empower brands to thrive — crafting assets that drive growth and make lasting impressions.
            </motion.p>

            <motion.div
              custom={0.55}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}
            >
              <Link to="/projects" className="btn btn-outline" style={{ fontSize: '15px', padding: '14px 30px' }}>
                See Our Work
                <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="btn btn-primary" style={{ fontSize: '15px', padding: '14px 30px' }}>
                <Zap size={16} />
                Work With Us
              </Link>
            </motion.div>
          </div>

          {/* Scroll indicator */}
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              position: 'absolute',
              bottom: '32px',
              color: 'var(--text-muted)',
            }}
          >
            <ChevronDown size={24} />
          </motion.div>
        </section>

        {/* ====================== STATS ====================== */}
        <section style={{ background: 'var(--bg-surface)', borderTop: '1px solid var(--border-card)', borderBottom: '1px solid var(--border-card)' }}>
          <div style={{
            maxWidth: 'var(--page-max-width)',
            margin: '0 auto',
            padding: 'clamp(32px, 4vw, 48px) var(--page-padding-x)',
            display: 'grid',
            gridTemplateColumns: `repeat(${isMobile ? 2 : 4}, 1fr)`,
            gap: '32px',
          }}>
            {stats.map(({ value, label }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                style={{ textAlign: 'center' }}
              >
                <div style={{
                  fontSize: 'clamp(28px, 4vw, 42px)',
                  fontWeight: 900,
                  letterSpacing: '-0.03em',
                  background: 'linear-gradient(135deg, var(--accent-gold) 0%, var(--accent-gold-light) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '4px',
                }}>
                  {value}
                </div>
                <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>{label}</div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ====================== SERVICES PREVIEW ====================== */}
        <section style={{ padding: 'var(--section-padding-y) var(--page-padding-x)', maxWidth: 'var(--page-max-width)', margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
            style={{ textAlign: 'center', marginBottom: 'clamp(32px, 4vw, 56px)' }}
          >
            <div className="section-tag" style={{ justifyContent: 'center' }}>
              <Zap size={13} />
              What We Build
            </div>
            <h2 style={{ fontSize: 'var(--text-h2)', fontWeight: 800, marginBottom: '14px' }}>
              End-to-End <span className="gradient-text">Digital Services</span>
            </h2>
            <p style={{ color: 'var(--text-secondary)', maxWidth: '480px', margin: '0 auto', fontSize: '15px' }}>
              From concept to launch — we handle every layer of the stack so you can focus on growing your brand.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '280px' : '320px'}, 1fr))`,
              gap: 'var(--card-gap)',
            }}
          >
            {services.map(({ icon, title, desc, color }) => (
              <motion.div
                key={title}
                variants={fadeUp}
                whileHover={isMobile ? {} : { y: -4, boxShadow: '0 12px 40px rgba(109,74,255,0.15)' }}
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-card)',
                  borderRadius: '14px',
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                  transition: 'border-color 0.2s',
                  cursor: 'default',
                }}
                onMouseEnter={e => !isMobile && (e.currentTarget.style.borderColor = 'var(--border-subtle)')}
                onMouseLeave={e => !isMobile && (e.currentTarget.style.borderColor = 'var(--border-card)')}
              >
                <div style={{
                  width: '46px', height: '46px', borderRadius: '12px',
                  background: `${color}18`,
                  border: `1px solid ${color}28`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: color,
                }}>
                  {icon}
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: 700 }}>{title}</h3>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.65', flex: 1 }}>{desc}</p>
                <Link
                  to="/services"
                  style={{
                    fontSize: '13px', fontWeight: 600, color: color,
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    transition: 'gap 0.2s',
                  }}
                >
                  Learn more <ArrowRight size={13} />
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            style={{ textAlign: 'center', marginTop: '40px' }}
          >
            <Link to="/services" className="btn btn-outline">
              View All Services
              <ArrowRight size={15} />
            </Link>
          </motion.div>
        </section>

        {/* ====================== PORTFOLIO PREVIEW ====================== */}
        <section style={{
          padding: 'var(--section-padding-y) var(--page-padding-x)',
          background: 'var(--bg-surface)',
          borderTop: '1px solid var(--border-card)',
          borderBottom: '1px solid var(--border-card)',
        }}>
          <div style={{ maxWidth: 'var(--page-max-width)', margin: '0 auto' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5 }}
              style={{ textAlign: 'center', marginBottom: 'clamp(32px, 4vw, 56px)' }}
            >
              <div className="section-tag" style={{ justifyContent: 'center' }}>
                <Zap size={13} />
                Recent Work
              </div>
              <h2 style={{ fontSize: 'var(--text-h2)', fontWeight: 800, marginBottom: '14px' }}>
                Projects We've <span className="gradient-text">Shipped</span>
              </h2>
              <p style={{ color: 'var(--text-secondary)', maxWidth: '440px', margin: '0 auto', fontSize: '15px' }}>
                A snapshot of our latest work — from AI tools to e-commerce platforms.
              </p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '280px' : '340px'}, 1fr))`,
                gap: 'var(--card-gap)',
              }}
            >
              {featuredProjects.map((project) => {
                const { title, subtitle, gradientBg, link, isInternal, isPrivate, featured } = project;
                return (
                <motion.div
                  key={title}
                  variants={fadeUp}
                  whileHover={isMobile ? {} : { y: -4 }}
                  onClick={() => setActiveProject(project)}
                  style={{
                    background: 'var(--bg-card)',
                    border: featured ? '1px solid rgba(245,158,11,0.45)' : '1px solid var(--border-card)',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    boxShadow: featured ? '0 0 18px rgba(245,158,11,0.08)' : 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => {
                    if (!isMobile) {
                      e.currentTarget.style.borderColor = featured ? 'rgba(245,158,11,0.75)' : 'var(--border-subtle)';
                      e.currentTarget.style.boxShadow = featured ? '0 8px 32px rgba(245,158,11,0.2)' : '0 8px 32px rgba(109,74,255,0.15)';
                    }
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = featured ? 'rgba(245,158,11,0.45)' : 'var(--border-card)';
                    e.currentTarget.style.boxShadow = featured ? '0 0 18px rgba(245,158,11,0.08)' : 'none';
                  }}
                >
                  <div style={{ aspectRatio: '16/9', overflow: 'hidden', position: 'relative', background: gradientBg || 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{
                      fontSize: 'clamp(14px, 2vw, 18px)',
                      fontWeight: 800,
                      color: 'rgba(255,255,255,0.75)',
                      textAlign: 'center',
                      padding: '0 20px',
                      letterSpacing: '-0.02em',
                    }}>
                      {title}
                    </span>
                    {featured && (
                      <div style={{
                        position: 'absolute', top: '10px', right: '10px',
                        background: 'rgba(245,158,11,0.15)', backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(245,158,11,0.4)',
                        padding: '3px 9px', borderRadius: '50px',
                        fontSize: '10px', fontWeight: 700, color: '#f5a623',
                        letterSpacing: '0.04em',
                      }}>
                        Featured
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '16px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>
                        {subtitle}
                      </p>
                      <h3 style={{ fontSize: '15px', fontWeight: 700 }}>{title}</h3>
                    </div>
                    {isPrivate ? (
                      <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '3px 8px', border: '1px solid var(--border-card)', borderRadius: '50px' }}>
                        Private
                      </span>
                    ) : isInternal ? (
                      <Link to={link} aria-label={`View ${title}`} style={{ color: 'var(--accent-purple-light)', transition: 'color 0.2s' }}>
                        <ArrowRight size={18} />
                      </Link>
                    ) : (
                      <a href={link} target="_blank" rel="noopener noreferrer" aria-label={`Visit ${title}`} style={{ color: 'var(--accent-purple-light)', transition: 'color 0.2s' }}>
                        <ExternalLink size={18} />
                      </a>
                    )}
                  </div>
                </motion.div>
              ); })}
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              style={{ textAlign: 'center', marginTop: '40px' }}
            >
              <Link to="/projects" className="btn btn-outline">
                View All Projects
                <ArrowRight size={15} />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* ====================== PRESS ====================== */}
        <section style={{ padding: 'var(--section-padding-y) var(--page-padding-x)', maxWidth: 'var(--page-max-width)', margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
            style={{ textAlign: 'center', marginBottom: 'clamp(32px, 4vw, 52px)' }}
          >
            <div className="section-tag" style={{ justifyContent: 'center' }}>
              <Zap size={13} />
              Press
            </div>
            <h2 style={{ fontSize: 'var(--text-h2)', fontWeight: 800 }}>
              As <span className="gradient-text">Featured In</span>
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: 'var(--card-gap)',
              maxWidth: '960px',
              margin: '0 auto',
            }}
          >
            {[
              {
                pub: 'GQ Magazine',
                quote: '"Marcus Elola is one of the programmers building romance-related tools for OpenAI\'s GPT Store. His \'Breakup Guide\' works with you to construct an \'empathetic and respectful\' letter to end a relationship. It was inspired by Elola\'s own experience in relationships that could have ended on better terms."',
                href: 'https://www.gq-magazine.co.uk/article/chatgpt-dating',
              },
              {
                pub: 'San Francisco Standard',
                quote: '"Others have created their own AI solutions meant for those in crisis. Marcus Elola, a programmer from Contra Costa County, spent \'a few hours\' last year building a \'suicide hotline\' GPT that talks users through their emotions and shares information about national crisis hotlines."',
                href: 'https://sfstandard.com/2025/08/26/ai-crisis-hotlines-suicide-prevention/',
              },
            ].map(({ pub, quote, href }) => (
              <motion.div
                key={pub}
                variants={fadeUp}
                whileHover={isMobile ? {} : { y: -4, boxShadow: '0 12px 40px rgba(109,74,255,0.15)' }}
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-card)',
                  borderRadius: '16px',
                  padding: 'clamp(24px, 3vw, 32px)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  transition: 'border-color 0.2s',
                }}
                onMouseEnter={e => !isMobile && (e.currentTarget.style.borderColor = 'var(--border-subtle)')}
                onMouseLeave={e => !isMobile && (e.currentTarget.style.borderColor = 'var(--border-card)')}
              >
                <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--accent-purple-light)' }}>{pub}</div>
                <p style={{ fontSize: '15px', lineHeight: '1.75', color: 'var(--text-secondary)', fontStyle: 'italic', flex: 1 }}>
                  {quote}
                </p>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    fontSize: '14px', fontWeight: 600, color: 'var(--accent-purple)',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.color = 'var(--accent-purple-light)')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--accent-purple)')}
                >
                  Read Full Article
                  <ExternalLink size={13} />
                </a>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* ====================== CTA BANNER ====================== */}
        <section style={{
          padding: 'clamp(60px, 8vw, 100px) var(--page-padding-x)',
          background: 'linear-gradient(135deg, rgba(109,74,255,0.15) 0%, rgba(90,63,224,0.08) 50%, rgba(245,158,11,0.05) 100%)',
          borderTop: '1px solid rgba(109,74,255,0.15)',
          borderBottom: '1px solid var(--border-card)',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <motion.div
            animate={{ scale: [1, 1.3, 1], opacity: [0.1, 0.2, 0.1] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '600px', height: '600px', borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(109,74,255,0.3) 0%, transparent 70%)',
              filter: 'blur(60px)', pointerEvents: 'none',
            }}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ position: 'relative', zIndex: 1 }}
          >
            <h2 style={{ fontSize: 'var(--text-h1)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '16px', maxWidth: '700px', margin: '0 auto 16px' }}>
              Ready to Build Something <span className="gradient-text-gold">Extraordinary?</span>
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'clamp(15px, 1.5vw, 17px)', marginBottom: '36px', maxWidth: '480px', margin: '0 auto 36px' }}>
              Let's turn your vision into digital reality. Every great project starts with a single conversation.
            </p>
            <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn btn-primary" style={{ fontSize: '16px', padding: '15px 34px' }}>
                <Zap size={17} />
                Start a Project
              </Link>
              <Link to="/chat" className="btn btn-outline" style={{ fontSize: '16px', padding: '15px 34px' }}>
                Chat With Our AI
                <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        </section>

        {/* ====================== CONTACT FORM ====================== */}
        <section style={{ padding: 'var(--section-padding-y) var(--page-padding-x)', maxWidth: '960px', margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
            style={{ textAlign: 'center', marginBottom: '36px' }}
          >
            <div className="section-tag" style={{ justifyContent: 'center' }}>
              <Zap size={13} />
              Contact
            </div>
            <h2 style={{ fontSize: 'var(--text-h2)', fontWeight: 800, marginBottom: '12px' }}>
              Get In <span className="gradient-text">Touch</span>
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>
              Tell us about your project and we'll craft a solution around your goals.
            </p>
          </motion.div>
          <ContactForm />
        </section>

      </div>

      {/* ====================== PROJECT MODAL ====================== */}
      <AnimatePresence>
        {activeProject && (
          <motion.div
            key="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setActiveProject(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '20px',
            }}
          >
            <motion.div
              key="modal-card"
              initial={{ opacity: 0, scale: 0.93, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.93, y: 20 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              onClick={e => e.stopPropagation()}
              style={{
                background: 'var(--bg-card)',
                border: activeProject.featured ? '1px solid rgba(245,158,11,0.5)' : '1px solid var(--border-subtle)',
                borderRadius: '20px',
                overflow: 'hidden',
                width: '100%',
                maxWidth: '520px',
                boxShadow: activeProject.featured
                  ? '0 24px 64px rgba(245,158,11,0.2), 0 0 0 1px rgba(245,158,11,0.1)'
                  : '0 24px 64px rgba(0,0,0,0.5)',
              }}
            >
              {/* Banner */}
              <div style={{
                aspectRatio: '16/7',
                background: activeProject.gradientBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative',
              }}>
                <span style={{
                  fontSize: 'clamp(18px, 4vw, 26px)', fontWeight: 900,
                  color: 'rgba(255,255,255,0.85)', letterSpacing: '-0.02em',
                  textAlign: 'center', padding: '0 24px',
                }}>
                  {activeProject.title}
                </span>
                {activeProject.featured && (
                  <div style={{
                    position: 'absolute', top: '12px', right: '12px',
                    background: 'rgba(245,158,11,0.15)', backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(245,158,11,0.4)',
                    padding: '4px 10px', borderRadius: '50px',
                    fontSize: '11px', fontWeight: 700, color: '#f5a623', letterSpacing: '0.04em',
                  }}>
                    Featured
                  </div>
                )}
                <button
                  onClick={() => setActiveProject(null)}
                  style={{
                    position: 'absolute', top: '12px', left: '12px',
                    background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(6px)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '50%', width: '32px', height: '32px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: '#fff',
                  }}
                >
                  <X size={15} />
                </button>
              </div>

              {/* Body */}
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <p style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '4px' }}>
                    {activeProject.subtitle}
                  </p>
                  <h3 style={{ fontSize: '20px', fontWeight: 800 }}>{activeProject.title}</h3>
                </div>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  {activeProject.description}
                </p>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {activeProject.isPrivate ? (
                    <span style={{
                      padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
                      color: 'var(--text-muted)', border: '1px solid var(--border-card)',
                    }}>
                      Private Project
                    </span>
                  ) : activeProject.isInternal ? (
                    <Link
                      to={activeProject.link}
                      onClick={() => setActiveProject(null)}
                      className="btn btn-primary"
                      style={{ fontSize: '14px', padding: '10px 22px' }}
                    >
                      <ArrowRight size={15} />
                      Open Project
                    </Link>
                  ) : (
                    <a
                      href={activeProject.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                      style={{ fontSize: '14px', padding: '10px 22px' }}
                    >
                      <ExternalLink size={15} />
                      Visit Site
                    </a>
                  )}
                  <button
                    onClick={() => setActiveProject(null)}
                    className="btn btn-outline"
                    style={{ fontSize: '14px', padding: '10px 22px' }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Home;
